'use client';

import { useMemo } from "react";
import { EventoPublicado } from "@/types";
import { useEvento } from "@/store/evento/evento";
import { parseFuncionDate } from "../utils/dateUtils";

interface Props {
    evento: EventoPublicado;
    onComprar: () => void;
    disabled?: boolean;
}

export const RightCardComprar = ({ evento, onComprar, disabled }: Props) => {
    const { state: funcion, set: setFuncion } = useEvento.use.funcion();

    // 1️⃣ FILTRO DE FECHAS CORREGIDO: Fusionando fecha y hora exacta
    const funcionesVigentes = useMemo(() => {
        if (!evento?.funciones) return [];

        const ahora = new Date();
        // Tolerancia de 2 horas restadas a la hora actual
        const limiteTolerancia = new Date(ahora.getTime() - (2 * 60 * 60 * 1000));

        return evento.funciones.filter((f: any) => {
            try {
                // Separamos la fecha pura (YYYY-MM-DD) por si viene con basura atrás
                const datePart = f.fecha.split('T')[0];
                // Agarramos la hora (HH:mm) o ponemos 00:00 por defecto
                const timePart = f.hora ? f.hora.slice(0, 5) : '00:00';

                // Armamos la fecha exacta del show: "2026-06-09T20:00:00"
                const fechaExactaStr = `${datePart}T${timePart}:00`;
                const fechaDeLaFuncion = new Date(fechaExactaStr);

                return fechaDeLaFuncion >= limiteTolerancia;
            } catch (error) {
                // Si alguna fecha viene rota de la BD, la mostramos por las dudas
                return true;
            }
        });
    }, [evento.funciones]);

    // Use selected function or fallback to first VIGENTE function
    const activeFuncion = funcion || funcionesVigentes[0];

    // Calcular el precio mínimo desde las entradas de la función activa
    let precioDesde = 90000;
    if (activeFuncion) {
        if (activeFuncion.entradas && activeFuncion.entradas.length > 0) {
            precioDesde = Math.min(...activeFuncion.entradas.map((e: any) => e.precio || Infinity));
            if (precioDesde === Infinity) precioDesde = 90000; // Recaudo por si no hay precios válidos
        } else if ((activeFuncion as any).precioDesde) {
            precioDesde = (activeFuncion as any).precioDesde;
        } else if (activeFuncion.precioBase) {
            precioDesde = activeFuncion.precioBase;
        }
    }

    return (
        <div className="space-y-4">
            <div className="rounded-lg overflow-hidden flex flex-col shadow-xl">
                <div className="bg-brand-red text-white p-5 flex justify-between items-baseline">
                    <span className="font-semibold text-lg">Entradas desde</span>
                    <span className="font-bold text-3xl">$ {precioDesde.toLocaleString("es-AR")}</span>
                </div>
                <div className="bg-brand-red/10 border border-brand-red-dark/30 border-t-0 p-6 shadow-sm shadow-brand-red-dark/10">

                    <div className="flex flex-col space-y-3 mb-6 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                        {/* 2️⃣ Usamos funcionesVigentes en vez de evento.funciones */}
                        {funcionesVigentes.map((f: any) => {
                            const { day, month, timeShow, timePuertas, dayOfWeek } = parseFuncionDate(f.fecha, f.hora);

                            const isSelected = activeFuncion?.id === f.id;
                            const agotado = f.status === 'agotado';

                            return (
                                <div
                                    key={f.id}
                                    onClick={() => !agotado && setFuncion(f)}
                                    className={`flex justify-between items-center p-2 rounded transition-colors border relative ${agotado
                                        ? 'opacity-50 cursor-not-allowed border-red-400'
                                        : isSelected
                                            ? 'border-[#c33232] bg-brand-red/15 cursor-pointer'
                                            : 'border-[#ff000014] hover:bg-brand-red/10 cursor-pointer'
                                        }`}
                                >
                                    <div className="text-center rounded px-3 py-2 bg-brand-red/15 min-w-[70px] transition-colors">
                                        <div className="font-bold text-lg text-white">{dayOfWeek} {day}</div>
                                        <div className="text-xs uppercase text-gray-400">{month}</div>
                                    </div>
                                    <div className="text-right flex-1">
                                        {agotado ? (
                                            <div className="text-center">
                                                <span className="text-xs font-bold text-red-500 bg-red-100/80 px-2 py-1 rounded">AGOTADO</span>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="text-[10px] text-red-500 font-bold tracking-widest mb-1">HORARIOS</div>
                                                <div className="text-[13px] font-semibold text-white flex space-x-3 justify-end">
                                                    {/* <span>{timePuertas}<br /><span className="text-xs font-normal text-gray-400">Puertas</span></span> */}
                                                    <span>{timeShow}<br /><span className="text-xs font-normal text-gray-400">Show</span></span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className={`relative rounded-lg p-[2px] ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
                        <div className="absolute inset-0 rounded-lg animate-border-glow"
                            style={{
                                background: 'conic-gradient(from 0deg, transparent, #e63946, #b72733, transparent)',
                            }}
                        />
                        <button
                            onClick={onComprar}
                            disabled={disabled}
                            className="relative w-full bg-brand-red uppercase hover:bg-[#c0392b] text-white font-bold py-3 rounded-[6px] transition-colors"
                        >
                            Comprar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};